// Docs CMS client — talks to the Spring API (/api/v1/docs/**).
// REACT_APP_API_URL points at the API; in local dev the CRA proxy forwards to :8080.

export const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const TOKEN_KEY = 'veritage_docs_jwt';

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

async function request(path, { method = 'GET', body, auth = false, raw = false } = {}) {
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  if (API_BASE.includes('ngrok')) headers['ngrok-skip-browser-warning'] = '1';

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let message = res.status === 401 || res.status === 403
      ? 'You need to sign in with a docs admin account.'
      : `Request failed (${res.status})`;
    try {
      const data = await res.json();
      message = data.detail || data.error || data.message || message;
    } catch {}
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return null;
  return raw ? res.text() : res.json();
}

const enc = encodeURIComponent;

/* ── Public ─────────────────────────────────────────────────────────────── */

export const docsApi = {
  products:    ()                       => request('/api/v1/docs/products'),
  nav:         (product, version)       => request(`/api/v1/docs/${enc(product)}/${enc(version)}/nav`),
  page:        (product, version, slug) => request(`/api/v1/docs/${enc(product)}/${enc(version)}/pages/${enc(slug)}`),
  searchIndex: (product, version)       => request(`/api/v1/docs/${enc(product)}/${enc(version)}/search-index`),
  exportUrl:   (product, version)       => `${API_BASE}/api/v1/docs/${enc(product)}/${enc(version)}/export.md`,
  exportText:  (product, version)       => request(`/api/v1/docs/${enc(product)}/${enc(version)}/export.md`, { raw: true }),
};

/* ── Admin ──────────────────────────────────────────────────────────────── */

const admin = (path, opts = {}) => request(`/api/v1/docs/admin${path}`, { ...opts, auth: true });

export const adminApi = {
  signIn: (email, password) => request('/signin', { method: 'POST', body: { email, password } }),
  me:     () => admin('/me'),
  tree:   () => admin('/tree'),

  createProduct: (body)          => admin('/products', { method: 'POST', body }),
  updateProduct: (id, body)      => admin(`/products/${enc(id)}`, { method: 'PATCH', body }),
  deleteProduct: (id)            => admin(`/products/${enc(id)}`, { method: 'DELETE' }),

  createVersion: (productId, body) => admin(`/products/${enc(productId)}/versions`, { method: 'POST', body }),
  updateVersion: (versionId, body) => admin(`/versions/${versionId}`, { method: 'PATCH', body }),
  deleteVersion: (versionId)       => admin(`/versions/${versionId}`, { method: 'DELETE' }),
  reorder:       (versionId, order)=> admin(`/versions/${versionId}/order`, { method: 'PUT', body: order }),

  createPage:  (versionId, body) => admin(`/versions/${versionId}/pages`, { method: 'POST', body }),
  page:        (id)              => admin(`/pages/${id}`),
  savePage:    (id, body)        => admin(`/pages/${id}`, { method: 'PATCH', body }),
  deletePage:  (id)              => admin(`/pages/${id}`, { method: 'DELETE' }),
  publish:     (id, note)        => admin(`/pages/${id}/publish`, { method: 'POST', body: { note } }),
  unpublish:   (id)              => admin(`/pages/${id}/unpublish`, { method: 'POST' }),
  discard:     (id)              => admin(`/pages/${id}/discard`, { method: 'POST' }),
  revisions:   (id)              => admin(`/pages/${id}/revisions`),
  restore:     (id, revId)       => admin(`/pages/${id}/revisions/${revId}/restore`, { method: 'POST' }),
};
